import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, RefreshCw, ShieldAlert, ShieldCheck, AlertTriangle } from 'lucide-react';
import { titanApi, ApiError } from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';

type ExitProtectionPayload = {
  controller_running?: boolean;
  market_session?: string;
  position_monitoring_enabled?: boolean;
  last_verification_at?: string | null;
  last_broker_stop_check_at?: string | null;
  stop_orders_placed?: number;
  stop_orders_failed?: number;
  hard_stop_monitor_active?: boolean;
  unprotected_count?: number;
  positions?: Array<{
    symbol: string;
    stop_loss_price: number;
    take_profit_price: number;
    sl_configured: boolean;
    broker_stop_present: boolean;
    virtual_stop_present: boolean;
    protection_ok: boolean;
    current_price?: number;
    pattern_name?: string;
  }>;
  message?: string;
};

const fmtTime = (iso: string | null | undefined) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/New_York',
  });
};

export default function ExitProtectionPanel() {
  const [data, setData] = useState<ExitProtectionPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = (await titanApi.exitProtection()) as ExitProtectionPayload;
      setData(res);
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const id = setInterval(() => void load(), 30_000);
    return () => clearInterval(id);
  }, [load]);

  const unprotected = data?.unprotected_count ?? 0;
  const monitoringOff = data?.position_monitoring_enabled === false;

  return (
    <div className="rounded-lg border bg-card p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-primary" />
          <div>
            <h2 className="text-sm font-semibold">Exit protection</h2>
            <p className="text-xs text-muted-foreground">
              Broker stops (RTH) + virtual stops · session {data?.market_session ?? '—'}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-2">Refresh</span>
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {monitoringOff && (
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertDescription>
            Position monitoring is OFF — stop-loss and take-profit will not trigger. Re-enable in Admin controls.
          </AlertDescription>
        </Alert>
      )}

      {unprotected > 0 && (
        <Alert className="border-amber-500/40 bg-amber-500/10">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription>
            {unprotected} position(s) missing stop coverage. Verification runs every 30s while the controller is running.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <div className="rounded border px-2 py-2">
          <div className="text-muted-foreground">Controller</div>
          <div className="font-semibold">{data?.controller_running ? 'Running' : 'Stopped'}</div>
        </div>
        <div className="rounded border px-2 py-2">
          <div className="text-muted-foreground">Last verify</div>
          <div className="font-semibold">{fmtTime(data?.last_verification_at)}</div>
        </div>
        <div className="rounded border px-2 py-2">
          <div className="text-muted-foreground">Stops placed</div>
          <div className="font-semibold">{data?.stop_orders_placed ?? 0}</div>
        </div>
        <div className="rounded border px-2 py-2">
          <div className="text-muted-foreground">Stop failures</div>
          <div className="font-semibold">{data?.stop_orders_failed ?? 0}</div>
        </div>
      </div>

      {!data?.controller_running && (
        <p className="text-sm text-muted-foreground">{data?.message ?? 'Start the controller to verify broker stops.'}</p>
      )}

      {(data?.positions?.length ?? 0) > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Symbol</TableHead>
              <TableHead>SL</TableHead>
              <TableHead>Broker stop</TableHead>
              <TableHead>Virtual stop</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data!.positions!.map((p) => (
              <TableRow key={p.symbol}>
                <TableCell className="font-mono font-medium">{p.symbol}</TableCell>
                <TableCell>
                  {p.sl_configured && p.stop_loss_price != null
                    ? `$${Number(p.stop_loss_price).toFixed(2)}`
                    : '—'}
                </TableCell>
                <TableCell>{p.broker_stop_present ? 'Yes' : 'No'}</TableCell>
                <TableCell>{p.virtual_stop_present ? 'Yes' : 'No'}</TableCell>
                <TableCell>
                  {p.protection_ok ? (
                    <Badge variant="secondary" className="text-emerald-700">Protected</Badge>
                  ) : (
                    <Badge variant="destructive">Gap</Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        data?.controller_running && (
          <p className="text-sm text-muted-foreground">No open stock positions.</p>
        )
      )}

      <p className="text-xs text-muted-foreground">
        Full performance analytics live on{' '}
        <Link to="/reports" className="text-primary underline-offset-4 hover:underline">
          Reports
        </Link>
        .
      </p>
    </div>
  );
}
